import { PayloadType } from '../../../Utility/SDK/PayloadType';
import {
    TActionType,
    TransactionType,
} from '../../../Utility/Tracking/DataSet/Transaction';
import { gameTracking } from '../../../Utility/Tracking/FBLegacyTracking';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { track } from '../../../Utility/Tracking/Track';
import { trackTransaction } from '../../../Utility/Tracking/TrackingHelpers/Transaction';
import { IPlayerState } from '../../Player/IPlayerState';
import {
    setBoosterJoker,
    setBoosterShuffle,
    setBoosterTNT,
    setCoins,
    setLives,
} from '../../Player/PlayerInventoryHelper';
import { IGameState } from '../IGameState';
import {
    EntryPoint,
    GiftType,
    IBotMessagePayload,
    ICrossPromoPayload,
    ICustomEntryPoint,
    isBotEntryPoint,
    isCrossPromoEntryPoint,
    isCustomEntryPoint,
    isCustomEntryPointLegacy,
    isRewardedEntryPoint,
    isRewardedEntryPointLegacy,
    isRewardedPayload,
} from './GameEntryPoint';
import { gateUnlockAddFriendRequest } from './PayloadAction/GateUnlockAddFriendRequest';
import { gateUnlockAddFriendResponse } from './PayloadAction/GateUnlockAddFriendResponse';
import { helpFriend } from './PayloadAction/HelpFriend';
import { requestLife } from './PayloadAction/RequestLife';
import { sendGift } from './PayloadAction/SendGift';
import { sendLife } from './PayloadAction/SendLife';

export function handleEntryPoint(
    state: IGameState,
    entryPoint: EntryPoint
): IGameState {
    // Delegate based on available keys.
    // This is a hack solution because there is no standard "type" to check in entry-point payloads.
    if (isCustomEntryPointLegacy(entryPoint)) {
        if (isRewardedEntryPointLegacy(entryPoint)) {
            state = this.handleInternalRewardedPayload(
                state,
                entryPoint.payload
            );
        } else {
            state = this.handleInternalPayload(state, entryPoint.payload);
        }
    } else if (isCustomEntryPoint(entryPoint)) {
        if (isRewardedEntryPoint(entryPoint)) {
            state = this.handleInternalRewardedPayload(state, entryPoint);
        } else {
            state = this.handleInternalPayload(state, entryPoint);
        }
    } else if (
        isBotEntryPoint(entryPoint) &&
        isRewardedPayload(entryPoint.gamePayload)
    ) {
        track('epBotMsgCategory', entryPoint.messageCategory);
        state = this.handleBotMessagePayload(state, entryPoint.gamePayload);
    } else if (isCrossPromoEntryPoint(entryPoint)) {
        state = this.handleCrossPromoPayload(state, entryPoint.gamePayload);
    }

    return state;
}

export function handleInternalRewardedPayload(
    state: IGameState,
    payload: ICustomEntryPoint
): IGameState {
    // Validate this 'message' has not already been claimed.
    if (
        state.playerState.receivedMessageIDs.indexOf(payload.messageId) !== -1
    ) {
        return state;
    }

    // Validate there is a userId with the payload and it is not the current user.
    if (!payload.userId || payload.userId === state.userState.userId) {
        return state;
    }

    // Add message ID to 'receivedMessageIDs' array.
    state = {
        ...state,
        playerState: {
            ...state.playerState,
            receivedMessageIDs: [
                ...state.playerState.receivedMessageIDs,
                payload.messageId,
            ],
        },
    };

    track('epCustomEntryPoint', payload.data);
    track('epSenderId', payload.userId);

    switch (payload.data) {
        case PayloadType.GATE_UNLOCK_ADD_FRIEND_RESPONSE:
            return gateUnlockAddFriendResponse(payload, state);
        case PayloadType.GATE_UNLOCK_ADD_FRIEND_REQUEST:
            return gateUnlockAddFriendRequest(payload, state);
        case PayloadType.SEND_LIFE:
            return sendLife(payload, state);
        case PayloadType.REQUEST_LIFE:
            return requestLife(payload, state);
        case PayloadType.HELP_FRIEND:
            return helpFriend(payload, state);
        case PayloadType.SEND_GIFT:
            return sendGift(payload, state);
    }
    return state;
}

export function handleInternalPayload(
    state: IGameState,
    payload: ICustomEntryPoint
): IGameState {
    track('epCustomEntryPoint', payload.data);
    if (payload.userId) {
        track('epSenderId', payload.userId);
    }
    switch (payload.data) {
        case PayloadType.SHARE_POSTCARD:
            gameTracking(FBTrackingEvent.Postcard_Entry);
            return state;
        case PayloadType.SHARE_PRIZEWHEEL:
            gameTracking(FBTrackingEvent.Prizewheel_Entry);
            return state;
    }
    return state;
}

export function handleBotMessagePayload(
    state: IGameState,
    payload: IBotMessagePayload
): IGameState {
    // Validate this 'gift' has not already been claimed.
    if (state.playerState.receivedMessageIDs.indexOf(payload.gift.id) !== -1) {
        return state;
    }

    // Add gift ID to 'receivedMessageIDs' array.
    state = {
        ...state,
        playerState: {
            ...state.playerState,
            receivedMessageIDs: [
                ...state.playerState.receivedMessageIDs,
                payload.gift.id,
            ],
        },
    };

    let playerState: IPlayerState = state.playerState;

    track('epBotMsgId', payload.gift.id.toString());

    payload.gift.giftData.forEach(gift => {
        switch (gift.itemId) {
            case GiftType.coins:
                playerState = setCoins(
                    playerState,
                    playerState.coins + gift.amount
                );
                track('epCoins', gift.amount);
                break;
            case GiftType.lives:
                playerState = setLives(
                    playerState,
                    playerState.lives + gift.amount
                );
                track('epLives', gift.amount);
                break;
            case GiftType.joker:
                playerState = setBoosterJoker(
                    playerState,
                    playerState.inventory.boosterJoker + gift.amount
                );
                track('epJoker', gift.amount);
                break;
            case GiftType.TNT:
                playerState = setBoosterTNT(
                    playerState,
                    playerState.inventory.boosterTNT + gift.amount
                );
                track('epMassRemove', gift.amount);
                break;
            case GiftType.Shuffle:
                playerState = setBoosterShuffle(
                    playerState,
                    playerState.inventory.boosterShuffle + gift.amount
                );
                track('epChanger', gift.amount);
                break;
        }
    });

    if (payload.gift.giftData.length > 0) {
        trackTransaction(state, TransactionType.REWARD, TActionType.BOT);
    }

    return {
        ...state,
        playerState: playerState,
    };
}

export function handleCrossPromoPayload(
    state: IGameState,
    payload: ICrossPromoPayload
): IGameState {
    gameTracking(FBTrackingEvent.Entries_xPromo, 1, {
        source: payload.originGameId,
        reward: (payload.promoReward ? 0 : 1).toString(),
    });

    track('epPromoSource', payload.originGameId);

    if (state.playerState.FTUE) {
        gameTracking(FBTrackingEvent.XPromo_new_user);
    } else {
        gameTracking(FBTrackingEvent.XPromo_old_user);
    }

    return state;
}
